import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import {
  formatDate,
  DateInput,
  DateInputType,
  PickerPosition,
  IconChangeLoClock,
  ButtonSecondary,
  IconArrowRight,
} from '@ui/src';
import { NotificationItemIssueStatus } from '../../hoc/center/types';
import NotificationEditColumnRow from './notification-edit-column-row';
import { NotificationEditColumnTypes } from './types';

import cn from './styles.module.css';

const NotificationEditColumn = ({
  notification,
  employees,
  auditLogAvailable,
  setChangeLogPanelIsOpen,
  editIssueControl,
  isDirty,
  onEditIssueSubmit,
  editError,
  updateInProgress,
  errors,
  editAvailable,
  setEditIssueValue,
  currentUser,
  editIssueFields,
}: NotificationEditColumnTypes) => (
  <div
    className={`${cn.notificationCenterColumn} ${cn.notificationCenterColumnSmall}`}
  >
    {notification && (
      <>
        <div className={cn.editFormContent}>
          <div className={cn.notificationCreatedDate}>
            {`Created ${formatDate(notification?.createdAt as string, true)}`}
          </div>
          {notification?.organization && (
            <div className={cn.notificationEditColumnFormGroup}>
              <div className={cn.title}>Customer</div>
              <NavLink
                to={`/customers/${
                  notification?.organization?.type === 'INDIVIDUAL' ||
                  notification?.organization?.type === 'COMPANY_OFFICIAL'
                    ? 'individual'
                    : 'corporate'
                }/${notification?.organization?.id}`}
                target="_blank"
                className={cn.organizationLink}
              >
                {notification?.organization?.firstName
                  ? `${notification?.organization?.firstName} ${notification?.organization?.lastName}`
                  : notification?.organization?.companyName}
                <IconArrowRight width={14} height={14} />
              </NavLink>
            </div>
          )}
          <NotificationEditColumnRow
            title="Status"
            list={[
              {
                name: `${NotificationItemIssueStatus.TODO.charAt(
                  0,
                )}${NotificationItemIssueStatus.TODO.slice(1).toLowerCase()}`,
                value: NotificationItemIssueStatus.TODO,
              },
              {
                name: NotificationItemIssueStatus.IN_PROGRESS.split('_')
                  .map(
                    (item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                  )
                  .join(' '),
                value: NotificationItemIssueStatus.IN_PROGRESS,
              },
              {
                name: `${NotificationItemIssueStatus.DONE.charAt(
                  0,
                )}${NotificationItemIssueStatus.DONE.slice(1).toLowerCase()}`,
                value: NotificationItemIssueStatus.DONE,
              },
              {
                name: `${NotificationItemIssueStatus.CANCELED.charAt(
                  0,
                )}${NotificationItemIssueStatus.CANCELED.slice(
                  1,
                ).toLowerCase()}`,
                value: NotificationItemIssueStatus.CANCELED,
              },
            ]}
            placeholder="Select status"
            editIssueControl={editIssueControl}
            name="status"
            error={errors?.status?.message}
            disabled={!editAvailable}
          />
          <NotificationEditColumnRow
            title="Responsible"
            list={employees}
            placeholder="Select Responsible"
            name="responsibleId"
            editIssueControl={editIssueControl}
            error={errors?.responsibleId?.message}
            disabled={!editAvailable}
            setEditIssueValue={setEditIssueValue}
            currentUser={currentUser}
            withAssignToMe={editIssueFields?.responsibleId !== currentUser?.id}
          />
          <NotificationEditColumnRow
            title="Executor"
            list={employees}
            placeholder="Select Executor"
            name="executorId"
            editIssueControl={editIssueControl}
            error={errors?.executorId?.message}
            disabled={!editAvailable}
            setEditIssueValue={setEditIssueValue}
            currentUser={currentUser}
            withAssignToMe={editIssueFields?.executorId !== currentUser?.id}
          />
          <div className={cn.notificationEditColumnFormGroup}>
            <div className={cn.title}>Deadline</div>
            <Controller
              control={editIssueControl}
              defaultValue=""
              name="deadline"
              render={({ field: { value, onChange } }) => (
                <div className={cn.deadlineWrapper}>
                  <DateInput
                    type={DateInputType.PSEUDO}
                    pickerPisition={PickerPosition.RIGHT}
                    pickerClassName={cn.editDeadlinePicker}
                    value={value}
                    placeholder="Select date"
                    className={cn.editDeadlinePickerWrapper}
                    onChange={(date) => onChange(date.toISOString())}
                    disabled={!editAvailable}
                    formatDateLong
                    withoutCustomSelector
                    withValueReset
                  />
                </div>
              )}
            />
            {errors?.deadline?.message && (
              <div className={cn.rowError}>{errors?.deadline?.message}</div>
            )}
          </div>
          <div className={cn.changeLogWrapper}>
            {auditLogAvailable && notification?.issue?.id && (
              <button
                type="button"
                onClick={() => setChangeLogPanelIsOpen(true)}
                className={cn.changeLogLinks}
              >
                <IconChangeLoClock />
                Change Log
              </button>
            )}
          </div>
        </div>
        {editError && <div className={cn.globalEditError}>{editError}</div>}
        {isDirty && editAvailable && (
          <ButtonSecondary
            className={cn.editSaveButton}
            text={updateInProgress ? 'Loading...' : 'Save'}
            onClick={onEditIssueSubmit}
          />
        )}
      </>
    )}
  </div>
);

export default NotificationEditColumn;
